import { Artifact } from 'components/Artifacts/types';
import { useEffect, useMemo, useState } from 'react';

type UseArtifactTranscriptionPollingOptions = {
  artifacts: Artifact[];
  refetch: () => Promise<unknown> | unknown;
  intervalMs?: number;
};

const ACTIVE_TRANSCRIPTION_STATUSES: Artifact['transcript']['status'][] = ['QUEUED', 'PROCESSING'];

export function useArtifactTranscriptionPolling({
  artifacts,
  refetch,
  intervalMs = 15000
}: UseArtifactTranscriptionPollingOptions) {
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => (typeof document === 'undefined' ? true : !document.hidden));

  const activeArtifactIds = useMemo(
    () =>
      artifacts
        .filter((artifact) => ACTIVE_TRANSCRIPTION_STATUSES.includes(artifact.transcript.status))
        .map((artifact) => artifact.id),
    [artifacts]
  );

  const hasActiveTranscriptions = activeArtifactIds.length > 0;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setIsDocumentVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!hasActiveTranscriptions || !isDocumentVisible) {
      return undefined;
    }

    void refetch();

    const timerId = window.setInterval(() => {
      void refetch();
    }, intervalMs);

    return () => {
      window.clearInterval(timerId);
    };
  }, [hasActiveTranscriptions, intervalMs, isDocumentVisible, refetch]);

  return {
    isPolling: hasActiveTranscriptions && isDocumentVisible,
    activeArtifactIds
  };
}

export default useArtifactTranscriptionPolling;